import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// COMPONENTS
import Box from '@/main/components/shared/box';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import { AreaButton } from '@/main/components/shared/styles';

// REDUX/UTILS/HOOKS
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { KeyValue } from '@/main/models/common';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { getDefaultCountryOptionDrop, getTranslateByKey, isNullUndefined, mountReducerWithData, removeEmptyValues } from '@/main/services/utils';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';

import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import useKogControlElementId from '@/main/hooks/useKogControlElementId';
import { DynamicReportGridRequest } from '@/main/models/request/dynamicReportGridRequest';
import { Controls } from '@/main/services/constants/constantsControl';
import { getOptionsStatusRegisterActiveInactive } from '@/main/services/data/data';
import { useStatusRegisterReducer } from '@/main/store/statusRegister';
import * as S from './styles';

interface IFiltersDynamicReportProps {
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getData: (filters: DynamicReportGridRequest) => void;
}

function FiltersDynamicReport({
  showFilters,
  getData,
  onResetSearch,
  setShowFilters
}: IFiltersDynamicReportProps) {
  //#region Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const SHOW_STATUS_DROP = useKogControlElementId(Controls.idCreateEditDinamicReport);
  //#endregion

  //#region Reducers
  const { countriesUser: countryReducer } = useCountryReducer();
  const { statusRegister: statusRegisterReducer } = useStatusRegisterReducer();
  //#endregion

  //#region States
  const [hasClear, setHasClear] = useState<boolean>(false);
  const [defaultValueEditVehicle, setDefaultValueEditVehicle] = useState<string>('');
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const optionStatus = getOptionsStatusRegisterActiveInactive();

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);
  const optionDefaultStatusDrop = optionStatus[0];

  const INITIAL_FORM_STATE = {
    country: null as KeyValue | null,
    reportName: '' as string,
    status: null as KeyValue | null,
  }
  type ISearchDynamicReportForm = typeof INITIAL_FORM_STATE;

  const methods = useFormQueryParams<ISearchDynamicReportForm>({
    onSubmit: onSubmit,
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer,
      status: mountReducerWithData(optionStatus)
    }
  })
  const {
    control,
    customHandleSubmit,
    setValue,
    getValues,
    clearErrors,
    reset,
    trigger,
    resetField,
    watch
  } = methods;
  const watcherCountry = watch('country');
  //#endregion

  //#region Core functions

  function handleClear() {
    onResetSearch();
    reset(INITIAL_FORM_STATE);

    clearErrors();
    setHasClear(true);

    if (optionDefaultCountryDrop) setValue('country', optionDefaultCountryDrop);

    if (optionDefaultCountryDrop !== null) {
      setValue('country', optionDefaultCountryDrop);
    }

    setValue('status', optionDefaultStatusDrop);
  }

  function onSubmit(formInputs: ISearchDynamicReportForm) {
    let request: DynamicReportGridRequest = {
      guidCountry: String(formInputs.country?.key),
      reportName: isNullUndefined(formInputs.reportName) ? "" : String(formInputs.reportName),
      idStatusRegister: isNullUndefined(formInputs.status) ? null : Number(formInputs.status.key)
    };

    if (!SHOW_STATUS_DROP) {
      request.idStatusRegister = Number(optionDefaultStatusDrop.key);
    }

    getData(removeEmptyValues(request));
    setShowFilters(false);
  }

  function InitalLoadData() {
    if (isNullUndefined(countryReducer.data)) return;

    onSubmit({ 
      country: optionDefaultCountryDrop, 
      reportName: '',
      status: optionDefaultStatusDrop
     });
  }

  // #endregion
  const disabledCountry = countryReducer.isLoading || isNullUndefined(countryReducer.data) || countryReducer.data.length === 1 || countryReducer.data.length === 0;

  const labelReporName = getTranslateByKey('information.name');

  // #region UseEffects
  useEffect(() => {
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
  }, []);

  useEffect(() => {
    InitalLoadData();
  }, [JSON.stringify(countryReducer)]);
  // #endregion

  return (
    <Box
      showBox={showFilters}
      title={getTranslateByKey('information.filters')}
    >
      <FormProvider {...methods as any} >
        <form onSubmit={customHandleSubmit} noValidate>
          <S.Container>
            <KogAutoCompleteControlled
              nameCustom="country"
              id="id-dynamic-report-select-country"
              isDefaultValueUnique
              setValueCustom={setValue}
              label={getTranslateByKey('information.country')}
              control={control}
              rulesCustom={{ required: true }}
              options={countryReducer.data}
              isLoading={countryReducer.isLoading}
              disabled={disabledCountry}
              onChangeCustom={(optionSelected: KeyValue | null) => { }}
            />
            <KogInputTextControlledNew
              name="reportName"
              label={labelReporName}
              control={control}
              id="id-dynamic-report-txt-reportName"
            />
            {SHOW_STATUS_DROP && (
              <KogAutoCompleteControlled
                nameCustom="status"
                id="id-dynamic-report-select-status"
                isDefaultValueUnique
                setValueCustom={setValue}
                label={getTranslateByKey('information.status')}
                control={control}
                rulesCustom={{ required: true }}
                options={optionStatus}
                isLoading={false}
                onChangeCustom={(optionSelected: KeyValue | null) => { }}
                valueDefaultKey={optionDefaultStatusDrop.key}
              />
            )}
            <S.AreaButtons>
              <SecondaryButton
                id="id-dynamic-report-btn-search"
                type="submit"
              >
                {getTranslateByKey('information.search')}
              </SecondaryButton>

              <SecondaryButton
                id="id-dynamic-report-btn-clear"
                type='button'
                onClick={handleClear}
              >
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </S.AreaButtons>

          </S.Container>
        </form>
      </FormProvider>

    </Box>
  )
}

function FiltersDynamicReportWrapper(props: Omit<IFiltersDynamicReportProps, 'showFilters' | 'setShowFilters'>) {
  const navigate = useNavigate();

  //#region States
  const [showFilters, setShowFilters] = useState<boolean>(true)
  //#endregion
  const SHOW_NEW_BUTTON = useKogControlElementId(Controls.idCreateEditDinamicReport);

  //#region HandleFunctions
  function handleFilters() {
    setShowFilters(prev => !prev)
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  //#endregion


  const mergedProps: IFiltersDynamicReportProps = {
    ...props,
    showFilters,
    setShowFilters
  }
  return (
    <>
      <AreaButton>
        {SHOW_NEW_BUTTON && (
          <Button onClick={() => navigate(RoutesConstants.dynamicReportConfiguration)}
            id="id-dynamic-report-btn-new">
            {getTranslateByKey('information.new')}
          </Button>
        )}
        <Button onClick={handleFilters}
          id="id-dynamic-report-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
      </AreaButton>
      <FiltersDynamicReport {...mergedProps} />
    </>
  )
}
export default FiltersDynamicReportWrapper